import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  getIdea, 
  updateIdea, 
  pinIdea, 
  fetchCurrentUser, 
  getIdeaComponents, 
  createComponent, 
  updateComponent, 
  deleteComponent, 
  deleteIdea 
} from '../api/ideas';
import { getComponentByType } from '../components/ComponentPicker/componentTypes';
import { handleErrorWithMonitoring } from '../utils/errorMonitor';
import { 
  FaTrash, 
  FaShare, 
  FaEyeSlash, 
  FaCopy, 
  FaFilePdf, 
  FaSort 
} from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';

export const useIdeaDashboardHandlers = ({
  id,
  token,
  setShowLoginModal,
  state,
  refreshSidebar,
  canEditIdea
}) => {
  const {
    // Destructure all state we need
    setIdea,
    setCurrentUser,
    setError,
    setTitle,
    setDescription,
    setIcon,
    setIsPinned,
    setTags,
    setComponents,
    components,
    setAnimateComponents,
    setShowEditModal,
    setShowDeleteConfirm,
    setShowShareModal,
    setShowAccessibilityModal,
    setShowOptionsMenu,
    setShowComponentPicker,
    setShowToast,
    setToastMessage,
    setIsReordering,
    setShowSearch,
    setSearchQuery,
    editModalTitle,
    editModalDescription,
    editModalIcon,
    editModalTags,
    editModalStatus,
    setEditModalTitle,
    setEditModalDescription,
    setEditModalIcon,
    setEditModalTags,
    setEditModalStatus,
    setEditModalNewTag,
    setEditingTagIndex,
    setEditingTagValue,
    setShowTagsSection,
    deleteConfirmText,
    setDeleteConfirmText,
    setMenuButtonPosition,
    menuButtonRef,
    showOptionsMenu,
    isReordering,
    draggedComponent,
    setDraggedComponent,
    setDragOverIndex,
    rightColumnWidth,
    setRightColumnWidth,
    setIsResizing,
    idea,
    title,
    description,
    icon,
    tags,
    isPinned,
    mobileChatOpen,
    setMobileChatOpen,
    mobileChatExpanded,
    setMobileChatExpanded,
    mobileChatMessage,
    setMobileChatMessage,
    searchQuery,
    showSearch,
    chatRef,
    filteredComponents,
    editModalNewTag
  } = state;

  const handleDeleteComponent = useCallback(async (componentId) => {
    try {
      if (!token) {
        console.error('No access token for deleteComponent');
        setShowLoginModal(true);
        return;
      }
      await deleteComponent(componentId, token);
      setComponents(components.filter((c) => c.id !== componentId));
    } catch (err) {
      handleErrorWithMonitoring(err, {
        setError,
        setShowLoginModal,
        setToastMessage,
        setShowToast
      }, 'handleDeleteComponent');
    }
  }, [token, components, setComponents, setShowLoginModal, setError, setToastMessage, setShowToast]);

  const handlePinIdea = useCallback(async () => {
    try {
      if (!token) {
        console.error('No access token for pinIdea');
        setShowLoginModal(true);
        return;
      }
      await pinIdea(id, !isPinned, token);
      setIsPinned(!isPinned);
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
    } catch (err) {
      handleErrorWithMonitoring(err, {
        setError,
        setShowLoginModal,
        setToastMessage,
        setShowToast
      }, 'handlePinIdea');
    }
  }, [token, id, isPinned, setIsPinned, setShowLoginModal, setError, setToastMessage, setShowToast, refreshSidebar]);

  const handleDeleteIdea = useCallback(async () => {
    if (deleteConfirmText !== idea.title) {
      setToastMessage('Please type the exact idea title to confirm deletion');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    try {
      if (!token) {
        console.error('No access token for deleteIdea');
        setShowLoginModal(true);
        return;
      }
      await deleteIdea(id, token);
      setShowDeleteConfirm(false);
      setShowOptionsMenu(false);
      
      setToastMessage('Idea deleted successfully');
      setShowToast(true);
      
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
      
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      console.error('Delete idea error:', err);
      if (err.message === 'Session expired, please log in again') {
        setShowLoginModal(true);
      } else {
        setToastMessage('Failed to delete idea: ' + (err.response?.data?.detail || err.message));
        setShowToast(true);
        setTimeout(() => setShowToast(false), 5000);
      }
    }
  }, [deleteConfirmText, idea, token, id, setShowDeleteConfirm, setShowOptionsMenu, setToastMessage, setShowToast, setShowLoginModal, refreshSidebar]);

  const handleDeleteIdeaConfirm = useCallback(() => {
    setShowDeleteConfirm(true);
    setDeleteConfirmText('');
    setShowOptionsMenu(false);
  }, [setShowDeleteConfirm, setDeleteConfirmText, setShowOptionsMenu]);

  const handleAddComponent = useCallback(async (type) => {
    try {
      if (!token) {
        console.error('No access token for createComponent');
        setShowLoginModal(true);
        return;
      }
      const componentInfo = getComponentByType(type);
      const newComponent = await createComponent(
        id,
        {
          type,
          title: componentInfo.name,
          position: components.length,
          is_public: true,
          data: componentInfo.defaultData || {},
        },
        token
      );
      setComponents([...components, newComponent]);
      setShowComponentPicker(false);
      setError('');
    } catch (err) {
      handleErrorWithMonitoring(err, {
        setError,
        setShowLoginModal,
        setToastMessage,
        setShowToast
      }, 'handleAddComponent');
    }
  }, [token, id, components, setComponents, setShowComponentPicker, setError, setShowLoginModal, setToastMessage, setShowToast]);

  const handleUpdateComponent = useCallback(async (componentId, updatedData) => {
    try {
      if (!token) {
        console.error('No access token for updateComponent');
        setShowLoginModal(true);
        return;
      }
      const { id, idea_id, last_edited_by, created_at, ...filteredData } = updatedData;
      const updatedComponent = await updateComponent(componentId, filteredData, token);
      setComponents(components.map((c) => (c.id === updatedComponent.id ? updatedComponent : c)));
    } catch (err) {
      handleErrorWithMonitoring(err, {
        setError,
        setShowLoginModal,
        setToastMessage,
        setShowToast
      }, 'handleUpdateComponent');
    }
  }, [token, components, setComponents, setShowLoginModal, setError, setToastMessage, setShowToast]);

  const handleShareIdea = useCallback(async () => {
    setShowShareModal(true);
    setShowOptionsMenu(false);
  }, [setShowShareModal, setShowOptionsMenu]);

  const handleShareLayout = useCallback(() => {
    state.setShowShareLayoutModal(true);
  }, [state]);

  const handleAccessibility = useCallback(() => {
    setShowAccessibilityModal(true);
    setShowOptionsMenu(false);
  }, [setShowAccessibilityModal, setShowOptionsMenu]);

  const handleEditIdea = useCallback(() => {
    setEditModalTitle(title);
    setEditModalDescription(description);
    setEditModalIcon(icon);
    setEditModalTags([...tags]);
    setEditModalStatus(idea?.status || 'draft');
    setEditModalNewTag('');
    setShowTagsSection(false);
    setShowEditModal(true);
    setShowOptionsMenu(false);
  }, [title, description, icon, tags, idea, setEditModalTitle, setEditModalDescription, setEditModalIcon, setEditModalTags, setEditModalStatus, setEditModalNewTag, setShowTagsSection, setShowEditModal, setShowOptionsMenu]);

  const handleEditModalSave = useCallback(async (customTheme = undefined) => {
    if (!editModalTitle.trim()) {
      setToastMessage('Title is required');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      return;
    }

    try {
      if (!token) {
        setShowLoginModal(true);
        return;
      }

      const updateData = {
        title: editModalTitle,
        description: editModalDescription,
        icon: editModalIcon,
        tags: editModalTags,
        status: editModalStatus
      };

      // Add custom_theme to update data if provided
      if (customTheme !== undefined) {
        updateData.custom_theme = customTheme;
      }

      await updateIdea(id, updateData, token);

      setTitle(editModalTitle);
      setDescription(editModalDescription);
      setIcon(editModalIcon);
      setTags(editModalTags);
      
      setShowEditModal(false);
      setToastMessage('Idea updated successfully!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      
      if (refreshSidebar) {
        refreshSidebar();
      }
    } catch (err) {
      console.error('Update failed:', err);
      if (err.message === 'Session expired, please log in again') {
        setShowLoginModal(true);
      } else {
        setToastMessage(err.message || 'Failed to update idea');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
      }
    }
  }, [editModalTitle, editModalDescription, editModalIcon, editModalTags, editModalStatus, token, id, setTitle, setDescription, setIcon, setTags, setShowEditModal, setToastMessage, setShowToast, setShowLoginModal, refreshSidebar]);

  const handleEditModalAddTag = useCallback((e) => {
    if (e.key === 'Enter' && editModalNewTag.trim() && !editModalTags.includes(editModalNewTag.trim())) {
      setEditModalTags([...editModalTags, editModalNewTag.trim()]);
      setEditModalNewTag('');
    }
  }, [editModalNewTag, editModalTags, setEditModalTags, setEditModalNewTag]);

  const handleEditModalDeleteTag = useCallback((tagToDelete) => {
    setEditModalTags(editModalTags.filter((tag) => tag !== tagToDelete));
  }, [editModalTags, setEditModalTags]);

  const handleEditTag = useCallback((index, tag) => {
    setEditingTagIndex(index);
    setEditingTagValue(tag);
  }, [setEditingTagIndex, setEditingTagValue]);

  const handleSaveTagEdit = useCallback((index) => {
    if (state.editingTagValue.trim() && !editModalTags.includes(state.editingTagValue.trim())) {
      const newTags = [...editModalTags];
      newTags[index] = state.editingTagValue.trim();
      setEditModalTags(newTags);
    }
    setEditingTagIndex(null);
    setEditingTagValue('');
  }, [state.editingTagValue, editModalTags, setEditModalTags, setEditingTagIndex, setEditingTagValue]);

  const handleCancelTagEdit = useCallback(() => {
    setEditingTagIndex(null);
    setEditingTagValue('');
  }, [setEditingTagIndex, setEditingTagValue]);

  // Mobile chat handlers
  const handleMobileChatToggle = useCallback(() => {
    if (mobileChatOpen && mobileChatMessage.trim()) {
      handleMobileChatSend();
    } else {
      setMobileChatOpen(!mobileChatOpen);
      if (mobileChatExpanded) {
        setMobileChatExpanded(false);
      }
    }
  }, [mobileChatOpen, mobileChatMessage, mobileChatExpanded, setMobileChatOpen, setMobileChatExpanded]);

  const handleMobileChatSend = useCallback(() => {
    if (mobileChatMessage.trim() && chatRef.current) {
      chatRef.current.sendMessage(mobileChatMessage.trim());
      setMobileChatMessage('');
    }
  }, [mobileChatMessage, chatRef, setMobileChatMessage]);

  const handleMobileChatKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleMobileChatSend();
    }
  }, [handleMobileChatSend]);

  // Search handlers
  const handleSearchToggle = useCallback(() => {
    setShowSearch(!showSearch);
    if (!showSearch) {
      setTimeout(() => {
        const searchInput = document.getElementById('component-search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    } else {
      setSearchQuery('');
    }
  }, [showSearch, setShowSearch, setSearchQuery]);

  const handleSearchBlur = useCallback(() => {
    if (!searchQuery.trim()) {
      setShowSearch(false);
    }
  }, [searchQuery, setShowSearch]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery]);

  const handleSearchKeyPress = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowSearch(false);
      setSearchQuery('');
    }
  }, [setShowSearch, setSearchQuery]);

  // Options menu handlers
  const handleOptionsMenuToggle = useCallback(() => {
    if (!showOptionsMenu && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      const menuWidth = 224;
      const isMobile = window.innerWidth <= 1024;
      
      let rightPosition = window.innerWidth - rect.right;
      let topPosition = rect.bottom + 8;
      
      if (isMobile) {
        if (rect.right + menuWidth > window.innerWidth) {
          rightPosition = 8;
        }
        if (rect.left - menuWidth < 0) {
          rightPosition = window.innerWidth - rect.left - menuWidth;
        }
        rightPosition = Math.max(8, Math.min(rightPosition, window.innerWidth - menuWidth - 8));
      }
      
      setMenuButtonPosition({ top: topPosition, right: rightPosition });
    }
    setShowOptionsMenu(!showOptionsMenu);
  }, [showOptionsMenu, menuButtonRef, setMenuButtonPosition, setShowOptionsMenu]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, component, index) => {
    if (!isReordering) return;
    setDraggedComponent({ component, index });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }, [isReordering, setDraggedComponent]);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDraggedComponent(null);
    setDragOverIndex(null);
  }, [setDraggedComponent, setDragOverIndex]);

  const handleDragOver = useCallback((e, index) => {
    if (!isReordering || !draggedComponent) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (index === -1) {
      setDragOverIndex(-1);
    } else if (index === components.length) {
      setDragOverIndex(components.length);
    } else {
      setDragOverIndex(index);
    }
  }, [isReordering, draggedComponent, components.length, setDragOverIndex]);

  const handleDragLeave = useCallback((e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  }, [setDragOverIndex]);

  const handleDrop = useCallback(async (e, dropIndex) => {
    e.preventDefault();
    if (!isReordering || !draggedComponent) {
      setDragOverIndex(null);
      return;
    }

    const { component: draggedComp, index: dragIndex } = draggedComponent;
    
    let actualDropIndex = dropIndex;
    if (dropIndex === -1) {
      actualDropIndex = 0;
    } else if (dropIndex === components.length) {
      actualDropIndex = components.length;
    }
    
    if (dragIndex === actualDropIndex) {
      setDragOverIndex(null);
      return;
    }

    const newComponents = [...components];
    newComponents.splice(dragIndex, 1);
    
    let adjustedDropIndex = actualDropIndex;
    if (dragIndex < actualDropIndex) {
      adjustedDropIndex = actualDropIndex;
    }
    
    newComponents.splice(adjustedDropIndex, 0, draggedComp);
    
    const updatedComponents = newComponents.map((comp, index) => ({
      ...comp,
      position: index
    }));

    setComponents(updatedComponents);
    setDragOverIndex(null);

    try {
      if (!token) {
        setShowLoginModal(true);
        return;
      }

      await Promise.all(
        updatedComponents.map((comp, index) => 
          updateComponent(comp.id, { ...comp, position: index }, token)
        )
      );
    } catch (err) {
      console.error('Failed to update component positions:', err);
      setToastMessage('Failed to save new order. Please try again.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      setComponents(components);
    }
  }, [isReordering, draggedComponent, components, setComponents, setDragOverIndex, token, setShowLoginModal, setToastMessage, setShowToast]);

  // Resize handler
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const startX = e.clientX;
    const startWidth = rightColumnWidth;
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = startX - moveEvent.clientX;
      const newWidth = Math.max(275, Math.min(750, startWidth + deltaX));
      setRightColumnWidth(newWidth);
      localStorage.setItem('rightColumnWidth', newWidth.toString());
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
    
    setIsResizing(true);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [rightColumnWidth, setRightColumnWidth, setIsResizing]);

  const handleReorderComponents = useCallback(() => {
    const newReorderState = !isReordering;
    setIsReordering(newReorderState);
    setShowOptionsMenu(false);
    
    if (newReorderState) {
      setShowSearch(false);
      setSearchQuery('');
      setToastMessage('Reordering mode activated! Drag components to reorder them.');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  }, [isReordering, setIsReordering, setShowOptionsMenu, setShowSearch, setSearchQuery, setToastMessage, setShowToast]);

  const handleUpdateIdeaFromShare = useCallback(async (updatedIdea) => {
    try {
      if (!token) {
        console.error('No access token for updateIdea');
        setShowLoginModal(true);
        return;
      }
      
      // Actually update the idea on the backend
      const updatedIdeaData = await updateIdea(id, updatedIdea, token);
      
      // Update local state with the response from backend
      setIdea(updatedIdeaData);
      setTitle(updatedIdeaData.title);
      setDescription(updatedIdeaData.description || '');
      setIcon(updatedIdeaData.icon || '😊');
      setTags(updatedIdeaData.tags || []);
      
      if (refreshSidebar) {
        refreshSidebar();
      }
      
      setError('');
      return updatedIdeaData;
    } catch (err) {
      const errorResult = handleErrorWithMonitoring(err, {
        setError,
        setShowLoginModal,
        setToastMessage,
        setShowToast
      }, 'handleUpdateIdeaFromShare');
      
      throw err; // Re-throw so the ShareModal can handle the error
    }
  }, [id, token, setShowLoginModal, setIdea, setTitle, setDescription, setIcon, setTags, setError, setToastMessage, setShowToast, refreshSidebar]);

  // Handle duplicate idea
  const handleDuplicateIdea = useCallback(async () => {
    try {
      if (!token) {
        console.error('No access token for duplicateIdea');
        setShowLoginModal(true);
        return;
      }
      // This would need a backend endpoint to duplicate an idea
      console.log('Duplicate idea functionality coming soon');
      setShowOptionsMenu(false);
      setToastMessage('Duplicate idea functionality coming soon!');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error('Duplicate idea error:', err);
      setToastMessage('Failed to duplicate idea');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  }, [token, setShowLoginModal, setShowOptionsMenu, setToastMessage, setShowToast]);

  // Handle export to PDF
  const handleExportToPDF = useCallback(async () => {
    try {
      setShowOptionsMenu(false);
      setToastMessage('Generating PDF export...');
      setShowToast(true);

      // Dynamic import for the PDF export utility
      const { exportIdeaToPDF } = await import('../utils/pdfExport');
      
      // Prepare idea data for export
      const ideaData = {
        title,
        description,
        icon,
        tags,
        status: idea?.status
      };

      const result = await exportIdeaToPDF(ideaData, components, state.currentUser);

      if (result.success) {
        setToastMessage(`PDF exported successfully! (${result.fileName})`);
      } else {
        setToastMessage(`Failed to export PDF: ${result.error}`);
      }
      
      setTimeout(() => setShowToast(false), 3000);

    } catch (err) {
      console.error('PDF export failed:', err);
      setToastMessage('Failed to export PDF. Please try again.');
      setTimeout(() => setShowToast(false), 3000);
    }
  }, [setShowOptionsMenu, setToastMessage, setShowToast, title, description, icon, tags, idea, components, state.currentUser]);

  // Dropdown menu component
  const DropdownMenu = useCallback(() => {
    if (!showOptionsMenu) return null;
    
    const isMobile = window.innerWidth <= 1024;
    const menuWidth = 224;
    const menuMaxHeight = 300;
    
    let safeTop = Math.max(8, Math.min(state.menuButtonPosition.top, window.innerHeight - menuMaxHeight - 16));
    let safeRight = state.menuButtonPosition.right;
    
    if (safeRight + menuWidth > window.innerWidth) {
      safeRight = window.innerWidth - menuWidth - 8;
    }
    if (safeRight < 8) {
      safeRight = 8;
    }
    
    return createPortal(
      <>
        {isMobile && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99998] animate-in fade-in-0 duration-200"
            onClick={() => setShowOptionsMenu(false)}
          />
        )}
        
        <div 
          data-dropdown-menu
          className={`fixed w-56 bg-[var(--bg-secondary)] rounded-xl shadow-xl z-[99999] border border-[var(--border-color)] animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200 ${
            isMobile ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2' : ''
          }`}
          style={isMobile ? {
            maxHeight: `${Math.min(menuMaxHeight, window.innerHeight - 100)}px`,
            overflowY: 'auto'
          } : {
            top: `${safeTop}px`,
            right: `${safeRight}px`,
            maxHeight: `${window.innerHeight - safeTop - 16}px`,
            overflowY: 'auto'
          }}
        >
          <div className="py-2">
            {canEditIdea ? (
              // Full menu for idea owners/collaborators
              <>
                <button onClick={handleEditIdea} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FiEdit2 className="mr-3 text-[var(--primary)]" /> Edit Idea
                </button>
                <button onClick={handleAccessibility} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaEyeSlash className="mr-3 text-[var(--primary)]" /> Accessibility
                </button>
                <button onClick={handleShareIdea} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaShare className="mr-3 text-[var(--primary)]" /> Share Idea
                </button>
                <button onClick={handleDuplicateIdea} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaCopy className="mr-3 text-[var(--primary)]" /> Duplicate Idea
                </button>
                <button onClick={handleExportToPDF} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaFilePdf className="mr-3 text-[var(--primary)]" /> Export to PDF
                </button>
                <button onClick={handleReorderComponents} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaSort className="mr-3 text-[var(--primary)]" /> {isReordering ? 'Stop Reordering' : 'Reorder Components'}
                </button>
                <div className="border-t border-[var(--border-color)] my-1"></div>
                <button onClick={handleDeleteIdeaConfirm} className="flex items-center px-4 py-3 text-sm w-full text-left transition-colors duration-150 hover:bg-[var(--bg-hover)]" style={{ color: 'var(--danger)' }}>
                  <FaTrash className="mr-3" /> Delete Idea
                </button>
              </>
            ) : (
              // Limited menu for viewers
              <>
                <button onClick={handleDuplicateIdea} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaCopy className="mr-3 text-[var(--primary)]" /> Duplicate Idea
                </button>
                <button onClick={handleExportToPDF} className="flex items-center px-4 py-3 text-sm text-[var(--text-primary)] hover:bg-[var(--bg-hover)] w-full text-left transition-colors duration-150">
                  <FaFilePdf className="mr-3 text-[var(--primary)]" /> Export to PDF
                </button>
              </>
            )}
          </div>
        </div>
      </>,
      document.body
    );
  }, [showOptionsMenu, state.menuButtonPosition, isReordering, setShowOptionsMenu, handleEditIdea, handleAccessibility, handleShareIdea, handleDuplicateIdea, handleExportToPDF, handleReorderComponents, handleDeleteIdeaConfirm]);

  return {
    handleDeleteComponent,
    handlePinIdea,
    handleDeleteIdea,
    handleDeleteIdeaConfirm,
    handleAddComponent,
    handleUpdateComponent,
    handleShareIdea,
    handleShareLayout,
    handleAccessibility,
    handleEditIdea,
    handleEditModalSave,
    handleEditModalAddTag,
    handleEditModalDeleteTag,
    handleEditTag,
    handleSaveTagEdit,
    handleCancelTagEdit,
    handleMobileChatToggle,
    handleMobileChatSend,
    handleMobileChatKeyPress,
    handleSearchToggle,
    handleSearchBlur,
    handleSearchChange,
    handleSearchKeyPress,
    handleOptionsMenuToggle,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleMouseDown,
    handleReorderComponents,
    handleUpdateIdeaFromShare,
    handleDuplicateIdea,
    handleExportToPDF,
    DropdownMenu
  };
};
